import throttle from 'lodash/throttle'
import { memo, useCallback, useEffect, useRef, useState } from 'react'

import { EventCode, trackInternalEvent } from '@rio/tracking'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import { i18n as I18n } from 'builder/utils/i18n'
import Preview from 'builder/components/Preview'

import EscapeHandler from 'builder/components/EscapeHandler'
import Icon, { Icon32 } from 'builder/components/Icon'

import { baseClient } from 'builder/modules/apiClient'
import { TemplateCategories } from 'builder/modules/resumeEditor'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { isResumeDocument } from 'builder/utils/getDocumentType'
import { PreviewRefProps } from 'builder/components/PreviewPanel'
import templateHeightFactorSelector from '../Helper/utils/templateHeightFactorSelector'
import { TemplatesPreviewList } from '../TemplatesList/TemplatesPreviewList'

import { Icon40 } from '../Icon/Icon'
import { DocumentCustomizationModalProps, RenderPreviewProp } from './types'
import ToolbarRight from './components/ToolbarRight'
import {
  Back,
  BackIcon,
  BackText,
  Container,
  Content,
  ContentInner,
  Main,
  Pagination,
  PaginationCounter,
  PaginationNext,
  PaginationPrev,
  PreviewBox,
  Sidebar,
  SidebarInner,
  Toolbar,
  ToolbarLeft,
} from './styles'
import { ColorSelection, ResumeSidebar } from './components/ResumeSidebar'
import { CUSTOMIZATION_MODAL_TEST_ID } from './constants'

type TemplateCategoriesType =
  | Record<string, never>
  | Record<`${TemplateCategories}`, { template_names: [] }>

const PREVIEW_WIDTH = 928

export const TEMPLATE_DATA: Record<
  `${TemplateCategories}`,
  { name: string; icon: JSX.Element; id: string }
> = {
  [TemplateCategories.all]: {
    name: 'All templates',
    icon: <Icon32.Templates width={24} height={24} />,
    id: TemplateCategories.all,
  },
  [TemplateCategories.simple]: {
    name: 'Simple',
    icon: <Icon.DoubleStars width={24} height={24} />,
    id: TemplateCategories.simple,
  },
  [TemplateCategories.ats]: {
    name: 'ATS',
    icon: <Icon.ArrowTarget width={24} height={24} color="#5C6373" />,
    id: TemplateCategories.ats,
  },
  [TemplateCategories.creative]: {
    name: 'Creative',
    icon: <Icon40.Unicorn width={24} height={24} color="#5C6373" />,
    id: TemplateCategories.creative,
  },
  [TemplateCategories.modern]: {
    name: 'Modern',
    icon: <Icon.HandModern width={24} height={24} />,
    id: TemplateCategories.modern,
  },
  [TemplateCategories.professional]: {
    name: 'Professional',
    icon: <Icon.WorkBag width={24} height={24} color="white" />,
    id: TemplateCategories.professional,
  },
  [TemplateCategories.free]: {
    name: 'Free',
    icon: <Icon.Deal width={24} height={24} />,
    id: TemplateCategories.free,
  },
}

export const DesktopDocumentCustomizationModal = memo((props: DocumentCustomizationModalProps) => {
  const {
    currentDocument,
    type,
    editorState,
    onClose,
    updateTemplate,
    handleFontChange,
    handleChangeLayoutFormat,
    handleColorSelect,
    handleSizeChange,
    handleSpacingChange,
    renderPreview,
    handleDownloadDocument,
    isSyncing,
    handleShareDocument,
    handleCombineDocuments,
    handleDownloadButtonMenuOpen,
    showResumeDistributionModal,
    handleSettingsFieldChange,
  } = props

  const contentRef = useRef<HTMLDivElement>(null)
  const sidebarRef = useRef<HTMLDivElement>(null)
  const previewBoxRef = useRef<HTMLDivElement>(null)
  const previewRef = useRef<PreviewRefProps>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPagesNumber, setTotalPagesNumber] = useState(1)
  const [width, setWidth] = useState(PREVIEW_WIDTH)
  const [height, setHeight] = useState(
    () => PREVIEW_WIDTH / templateHeightFactorSelector(currentDocument),
  )
  const [templateCategories, setTemplateCategories] = useState<TemplateCategoriesType>({})
  const ui = useTypedSelector(state => state.ui)
  const isResume = isResumeDocument(currentDocument)
  const isDataLoaded = currentDocument && editorState.isLoaded
  const hasPreviousPage = currentPage > 1
  const hasNextPage = currentPage < totalPagesNumber

  useEffect(() => {
    baseClient
      .get<{ grouped_categories: Record<`${TemplateCategories}`, { template_names: [] }> }>(
        'templates/categories',
      )
      .then(response => {
        setTemplateCategories({
          ...response.data.grouped_categories,
          free: { template_names: [] },
        })
      })
  }, [])

  const handlePreviewResize = useCallback(() => {
    if (!previewBoxRef.current) return
    const previewWidth = previewBoxRef.current.offsetWidth
    const width = Math.min(previewWidth, PREVIEW_WIDTH)
    const height =
      Math.min(previewWidth, PREVIEW_WIDTH) / templateHeightFactorSelector(currentDocument)

    setWidth(width)
    setHeight(height)

    if (!previewRef.current) return
    previewRef.current?.resize(width)
  }, [currentDocument])

  useEffect(() => {
    const handlePreviewResizeThrottled = throttle(handlePreviewResize, 50)
    window.addEventListener('resize', handlePreviewResizeThrottled)
    handlePreviewResizeThrottled()
    return () => {
      window.removeEventListener('resize', handlePreviewResizeThrottled)
    }
  }, [handlePreviewResize])

  useEffect(() => {
    const element = contentRef.current
    if (!element) return
    disableBodyScroll(element)
    return () => {
      enableBodyScroll(element)
    }
  }, [])

  useEffect(() => {
    const element = sidebarRef.current
    if (!element) return
    disableBodyScroll(element)
    return () => {
      enableBodyScroll(element)
    }
  }, [])

  const handlePaginationStateChange = useCallback(
    (state: { currentPage: number; totalPagesCount: number }) => {
      setCurrentPage(state.currentPage + 1)
      setTotalPagesNumber(state.totalPagesCount)
    },
    [],
  )

  const handlePreviousPageClick = useCallback(() => {
    if (!previewRef.current) return
    previewRef.current?.previousPage()
    trackInternalEvent(`change_${type}_preview_page` as EventCode)
  }, [type])

  const handleNextPageClick = useCallback(() => {
    if (!previewRef.current) return
    previewRef.current?.nextPage()
    trackInternalEvent(`change_${type}_preview_page` as EventCode)
  }, [type])

  const defaultRenderPreview: RenderPreviewProp = () => {
    return (
      <Preview
        {...{ height, width, type }}
        document={currentDocument}
        documentId={currentDocument.id}
        ref={previewRef}
        onPaginationChange={handlePaginationStateChange}
      />
    )
  }

  const finalRenderPreview = renderPreview || defaultRenderPreview

  return (
    <Container data-testid={CUSTOMIZATION_MODAL_TEST_ID}>
      <EscapeHandler onPress={onClose} />
      <Toolbar>
        <ToolbarLeft>
          <Back onClick={onClose} id="back-to-editor-button" data-testid="back-to-editor-button">
            <BackIcon />
            <BackText>{I18n.t('builder.resume_editor.back_to_editor')}</BackText>
          </Back>
        </ToolbarLeft>
        <ToolbarRight
          isSyncing={isSyncing}
          handleDownloadDocument={handleDownloadDocument}
          handleShareDocument={handleShareDocument}
          handleCombineDocuments={handleCombineDocuments}
          handleDownloadButtonMenuOpen={handleDownloadButtonMenuOpen}
          showResumeDistributionModal={showResumeDistributionModal}
        />
      </Toolbar>

      <Main>
        {isResume ? (
          <ResumeSidebar
            {...{
              resume: currentDocument,
              updateTemplate,
              handleFontChange,
              handleChangeLayoutFormat,
              handleColorSelect,
              handleSizeChange,
              handleSpacingChange,
              handleSettingsFieldChange,
            }}
          />
        ) : (
          <Sidebar>
            <ColorSelection
              {...{
                handleColorSelect,
                currentDocument,
              }}
            />
            <SidebarInner ref={sidebarRef}>
              <TemplatesPreviewList
                selectedTemplates={templateCategories[TemplateCategories.all]?.template_names || []}
                selectedCategory={TemplateCategories.all}
                currentDocument={currentDocument}
                updateTemplate={updateTemplate}
              />
            </SidebarInner>
          </Sidebar>
        )}
        <Content>
          <ContentInner ref={contentRef}>
            <PreviewBox ref={previewBoxRef}>
              {isDataLoaded &&
                finalRenderPreview({
                  height,
                  width,
                  type,
                  document: currentDocument,
                  documentId: currentDocument.id,
                  previewRef,
                  onPaginationChange: handlePaginationStateChange,
                })}
            </PreviewBox>
          </ContentInner>

          <Pagination isVisible={!ui?.isSnackBarOpen}>
            <PaginationPrev onClick={handlePreviousPageClick} isDisabled={!hasPreviousPage}>
              <Icon.Chevron />
            </PaginationPrev>
            <PaginationCounter>
              {currentPage} / {totalPagesNumber}
            </PaginationCounter>
            <PaginationNext onClick={handleNextPageClick} isDisabled={!hasNextPage}>
              <Icon.Chevron />
            </PaginationNext>
          </Pagination>
        </Content>
      </Main>
    </Container>
  )
})
